import { useApiMutation, useApiMutationWithBaseUrl } from '@/hooks/useApi';
import { isStaffMode } from '@/util/staffUtils';
import { useFastApiUrl } from '@/util/fastApiUrl';
import { useTheme, alpha } from '@mui/material';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import type { ApexOptions } from 'apexcharts';
import { useEffect, useMemo, useRef, useState } from 'react';
import Chart from 'react-apexcharts';
import ReactMarkdown from 'react-markdown';
import { getMarkdownProps } from './markdownProps';

export type ChartType =
  | 'pie'
  | 'donut'
  | 'bar'
  | 'bar-stacked'
  | 'bar-100'
  | 'column'
  | 'column-stacked'
  | 'column-100'
  | 'line'
  | 'area'
  | 'scatter'
  | 'heatmap'
  | 'treemap'
  | 'radar';

export interface GraphData {
  uiText?: {
    title?: string;
    subtitle?: string;
    legendTitle?: string;
    unitLabelPlural?: string;
    unitLabelSingular?: string;
    totalLabel?: string;
    donutCenterLabel?: string;
    levelLabelPrefix?: string;
  };
  data: {
    columns: Array<{ name: string; type: 'number' | 'string' | 'date' }>;
    rows: any[][];
    totals?: Record<string, number>;
  };
  chart: {
    library: 'apex';
    type: ChartType;
    options: ApexOptions;
    series: any;
  };
  insightsMarkdown?: string | null;
  accessibilityTable?: boolean;
  legendPanel?: {
    title?: string;
    items?: Array<{ left: string; right?: string }>;
    footer?: { left: string; right: string };
  };
}

export interface GraphApiResponse {
  graph: GraphData;
}

interface GenerateGraphPayload {
  question: string;
  answer: string;
  sql_query: string;
  sql_result: string;
}

const apexTypeFrom = (t: ChartType): string => {
  if (t.includes('bar') || t.includes('column')) return 'bar';
  if (
    ['pie', 'donut', 'line', 'area', 'radar', 'heatmap', 'scatter'].includes(t)
  )
    return t;
  return 'treemap';
};

const GraphSkeleton: React.FC<{ isDark: boolean }> = ({ isDark }) => {
  const skeletonBg = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';

  return (
    <Box
      sx={{
        width: '100%',
        height: '240px',
        mt: 1,
        mb: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
        bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#F8F9FA',
      }}
    >
      <Skeleton
        variant="rectangular"
        sx={{ bgcolor: skeletonBg, borderRadius: 1, height: '100%' }}
        width="100%"
      />
    </Box>
  );
};

const GraphRenderer: React.FC<{
  isGraph?: boolean;
  question?: string;
  answer?: string;
  sqlQuery?: string;
  tableName?: string;
  sqlResult?: string[] | null;
  savedGraphData?: GraphData | null;
  messageId?: string;
  conversationId?: string;
}> = ({
  isGraph,
  question,
  answer,
  sqlQuery,
  tableName,
  sqlResult,
  savedGraphData,
  messageId,
  conversationId,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const fastApiUrl = useFastApiUrl();

  const activeGreen = '#2E7D32';
  const emptySegmentColor = isDark ? '#373D3F' : '#E0E0E0';
  const chartLabelColor = isDark ? '#FFFFFF' : '#2D3436';
  const chartSubLabelColor = isDark ? alpha('#FFFFFF', 0.7) : '#636E72';

  // Track if graph API call has completed (success or error)
  const [isGraphReady, setIsGraphReady] = useState(!!savedGraphData);

  // Track if graph data has already been saved to DB to avoid duplicate calls
  const graphSavedRef = useRef(false);

  const {
    mutate: generateGraph,
    data: graph,
    isPending: isLoading,
    isError,
    isSuccess,
  } = useApiMutationWithBaseUrl<GraphApiResponse, GenerateGraphPayload>({
    baseURL: fastApiUrl,
    url: '/analytics/generate-graph',
    method: 'post',
  });

  // Mutation for saving graph data to DB
  const { mutateAsync: saveGraphData } = useApiMutation<any, any>({
    url: '/user/updateGraphData',
    method: 'put',
  });

  useEffect(() => {
    // If not a graph query, render children immediately (no loading state).
    if (!isGraph) {
      setIsGraphReady(true);
      return;
    }

    // If we already have saved graph data from DB, use it directly
    if (savedGraphData) {
      setIsGraphReady(true);
      return;
    }

    const hasRequiredGraphPayload =
      !!question && !!answer && !!sqlQuery && !!tableName && !!sqlResult;
    if (!hasRequiredGraphPayload) {
      setIsGraphReady(true);
      return;
    }

    setIsGraphReady(false);
    graphSavedRef.current = false;
    generateGraph({
      question: question,
      answer: answer,
      sql_query: sqlQuery,
      sql_result: JSON.stringify(sqlResult),
    });
  }, [
    isGraph,
    question,
    answer,
    sqlQuery,
    tableName,
    generateGraph,
    sqlResult,
    savedGraphData,
  ]);

  // Update ready state when API call completes
  useEffect(() => {
    if (isSuccess || isError) {
      setIsGraphReady(true);
    }
  }, [isSuccess, isError]);

  // Save graph data to DB after successful generation (skip in staff mode)
  useEffect(() => {
    if (
      isSuccess &&
      graph?.graph &&
      messageId &&
      conversationId &&
      !savedGraphData &&
      !graphSavedRef.current &&
      !isStaffMode()
    ) {
      graphSavedRef.current = true;
      saveGraphData({
        id: messageId,
        conversationId: conversationId,
        graphData: graph.graph,
      }).catch((error: any) => {
        console.error('Error saving graph data:', error);
        graphSavedRef.current = false;
      });
    }
  }, [
    isSuccess,
    graph,
    messageId,
    conversationId,
    savedGraphData,
    saveGraphData,
  ]);

  const parseFunction = (funcStr: any) => {
    if (funcStr === undefined || funcStr === null) return undefined;
    if (typeof funcStr !== 'string') return funcStr;
    try {
      if (funcStr.includes('function'))
        return new Function(`return ${funcStr}`)();
      return () => funcStr;
    } catch (e) {
      return () => funcStr;
    }
  };

  // Use saved graph data from DB if available, otherwise use freshly generated data
  const graphData = savedGraphData || graph?.graph;

  const finalOptions = useMemo(() => {
    if (!graphData?.chart?.options) return {};

    const opts = JSON.parse(JSON.stringify(graphData.chart.options));
    const apiColors = opts.colors || [activeGreen, emptySegmentColor];
    const apiStroke = opts.stroke;
    const apiDonutLabels = opts?.plotOptions?.pie?.donut?.labels;
    const apiDonutSize = opts?.plotOptions?.pie?.donut?.size || '75%';
    const apiTotalLabel =
      apiDonutLabels?.total?.label ||
      graphData.uiText?.donutCenterLabel ||
      'Total Progress';

    const merged: ApexOptions = {
      ...opts,
      chart: {
        background: 'transparent',
        toolbar: { show: false },
        fontFamily: theme.typography.fontFamily,
        ...opts?.chart,
      },
      labels: opts.labels,
      colors: apiColors,
      theme: { mode: isDark ? 'dark' : 'light' },
      stroke:
        apiStroke?.show === false
          ? { show: false }
          : {
              show: true,
              width: apiStroke?.width ?? 2,
              colors: apiStroke?.colors || [isDark ? '#121212' : '#FFFFFF'],
            },
      plotOptions: {
        pie: {
          expandOnClick: false,
          donut: {
            size: apiDonutSize,
            labels: {
              show: apiDonutLabels?.show !== false,
              name: {
                show: apiDonutLabels?.name?.show !== false,
                color: apiDonutLabels?.name?.color || chartSubLabelColor,
                offsetY: apiDonutLabels?.name?.offsetY ?? -8,
                fontSize: apiDonutLabels?.name?.fontSize || '14px',
              },
              value: {
                show: apiDonutLabels?.value?.show !== false,
                color: apiDonutLabels?.value?.color || chartLabelColor,
                fontSize: apiDonutLabels?.value?.fontSize || '24px',
                fontWeight: 800,
                offsetY: apiDonutLabels?.value?.offsetY ?? 8,
                formatter: parseFunction(apiDonutLabels?.value?.formatter),
              },
              total: {
                show: apiDonutLabels?.total?.show !== false,
                label: apiTotalLabel,
                color: apiDonutLabels?.total?.color || chartSubLabelColor,
                formatter: parseFunction(apiDonutLabels?.total?.formatter),
              },
            },
          },
        },
      },
      legend: { show: opts.legend?.show ?? false, ...opts.legend },
      dataLabels: { enabled: opts.dataLabels?.enabled ?? false },
      tooltip: {
        enabled: opts.tooltip?.enabled ?? true,
        theme: isDark ? 'dark' : 'light',
      },
      states: {
        hover: { filter: { type: 'none' } },
        active: { filter: { type: 'none' } },
      },
    };

    return merged;
  }, [
    graphData,
    isDark,
    theme,
    activeGreen,
    emptySegmentColor,
    chartLabelColor,
    chartSubLabelColor,
  ]);

  // If not a graph query, render children immediately
  if (!isGraph) {
    return null;
  }

  // Show skeleton while loading
  if (isLoading || (!graphData && !isError && !isGraphReady)) {
    return <GraphSkeleton isDark={isDark} />;
  }

  // If error or no valid chart data, only render children when ready
  if (isError || !graphData?.chart) {
    return isGraphReady ? null : <GraphSkeleton isDark={isDark} />;
  }

  const isDonutLayout =
    ['donut', 'pie'].includes(graphData.chart.type) && !!graphData.legendPanel;

  return (
    <>
      <Box sx={{ width: '100%', mt: 2, mb: 4 }}>
        {graphData.uiText?.title && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              {graphData.uiText.title}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {graphData.uiText.subtitle}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: isDonutLayout ? 'flex' : 'block',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 4, md: 6 },
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box
            sx={{
              flex: '0 0 auto',
              width: isDonutLayout ? { xs: '100%', md: 360 } : '100%',
            }}
          >
            <Chart
              options={finalOptions}
              series={graphData.chart.series}
              type={apexTypeFrom(graphData.chart.type) as any}
              width="100%"
              height={340}
            />
          </Box>

          {graphData.legendPanel && (
            <Box sx={{ flex: 1, width: '100%', minWidth: 280 }}>
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 900,
                  mb: 2,
                  display: 'block',
                  color: 'text.secondary',
                }}
              >
                {graphData.legendPanel.title || 'Breakdown'}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {graphData.legendPanel.items?.map((item, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                      pb: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {item.left}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {item.right}
                    </Typography>
                  </Box>
                ))}
              </Box>
              {graphData.legendPanel.footer && (
                <Box
                  sx={{
                    mt: 3,
                    pt: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    {graphData.legendPanel.footer.left}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 900, color: 'primary.main' }}
                  >
                    {graphData.legendPanel.footer.right}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {graphData.insightsMarkdown && (
          <Box
            sx={{
              mt: 4,
              p: 2,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: isDark ? alpha('#fff', 0.03) : '#F8F9FA',
            }}
          >
            <ReactMarkdown {...getMarkdownProps}>
              {graphData.insightsMarkdown}
            </ReactMarkdown>
          </Box>
        )}
      </Box>
    </>
  );
};

export default GraphRenderer;
